import { useEffect, useRef, useState } from 'react'
import { useContent } from '../../contexts/ContentContext'
import SectionWrapper from '../ui/SectionWrapper'
import SectionLabel from '../ui/SectionLabel'
import TopicCard from '../ui/TopicCard'
import { ArrowUpRight, X } from 'lucide-react'

function useBookingModal(calendarUrl) {
  const [isOpen, setIsOpen] = useState(false)
  const [shouldMount, setShouldMount] = useState(false)
  const [origin, setOrigin] = useState({ x: '50%', y: '100%' })
  const buttonRef = useRef(null)

  // Defer iframe mounting so hero video / initial paint are not disturbed.
  // Mount when the browser is idle, or at latest after 4s, or immediately on open.
  useEffect(() => {
    if (shouldMount) return
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1))
    const cic = window.cancelIdleCallback || clearTimeout
    const idleId = ric(() => setShouldMount(true), { timeout: 4000 })
    return () => cic(idleId)
  }, [shouldMount])

  const openFromButton = () => {
    const btn = buttonRef.current
    if (btn) {
      const r = btn.getBoundingClientRect()
      setOrigin({ x: `${r.left + r.width / 2}px`, y: `${r.top + r.height / 2}px` })
    }
    setShouldMount(true)
    setIsOpen(true)
  }

  const close = () => setIsOpen(false)

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && close()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Lock page scroll while booking modal is open
  useEffect(() => {
    if (!isOpen) return
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.overflow
    const prevBody = body.style.overflow
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    return () => {
      html.style.overflow = prevHtml
      body.style.overflow = prevBody
    }
  }, [isOpen])

  useEffect(() => {
    const handler = () => {
      const bookingEl = document.getElementById('booking')
      if (bookingEl) bookingEl.scrollIntoView({ behavior: 'smooth' })
      setTimeout(openFromButton, 650)
    }
    window.addEventListener('booking:open', handler)
    return () => window.removeEventListener('booking:open', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Inset sheet + blur backdrop. Close above burger (z-[110]+).
  // Backdrop captures pointer so page cannot scroll/click through.
  const overlay = calendarUrl && shouldMount ? (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[105] bg-black/50 backdrop-blur-md animate-fadeIn"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Calendar sheet — inset below burger; centered via left/right + max-w (no translate fight with scale) */}
      <div
        className="fixed z-[110] top-14 bottom-4 left-3 right-3 sm:top-16 sm:bottom-6 sm:left-6 sm:right-6 md:top-20 md:bottom-8 mx-auto max-w-[920px] overflow-hidden bg-transparent"
        style={{
          transform: isOpen ? 'scale(1)' : 'scale(0.05)',
          transformOrigin: `${origin.x} ${origin.y}`,
          pointerEvents: isOpen ? 'auto' : 'none',
          opacity: isOpen ? 1 : 0,
          transition: isOpen
            ? 'transform 500ms cubic-bezier(0.16,1,0.3,1), opacity 300ms ease'
            : 'transform 500ms cubic-bezier(0.16,1,0.3,1), opacity 300ms ease',
        }}
        role={isOpen ? 'dialog' : undefined}
        aria-modal={isOpen ? true : undefined}
        aria-label={isOpen ? 'Booking calendar' : undefined}
      >
        <iframe
          id="fluentbooking"
          loading="eager"
          src={calendarUrl}
          className="w-full h-full border-0 bg-transparent"
          style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
          title="Booking calendar"
          data-testid="booking-iframe"
        />
      </div>

      {/* Close — above sheet and burger (burger is z-[100]) */}
      {isOpen && (
        <button
          type="button"
          onClick={close}
          className="fixed top-3 right-3 md:top-4 md:right-4 z-[120]
            w-10 h-10 min-w-10 min-h-10 rounded-full
            bg-modal-close md:bg-modal-close-strong hover:bg-primary-on-dark
            flex items-center justify-center transition-colors duration-200
            shadow-lg border border-color-light"
          aria-label="Close"
          data-testid="booking-close"
        >
          <X size={18} className="md:hidden text-color-primary" />
          <X size={20} className="hidden md:block text-color-primary" />
        </button>
      )}
    </>
  ) : null

  return { open: openFromButton, buttonRef, overlay }
}

export default function BookingSection() {
  const { booking } = useContent()
  const { open, buttonRef, overlay } = useBookingModal(booking.calendarUrl)

  return (
    <SectionWrapper data-section="BookingSection" id="booking" data-testid="booking-section">
      <div className="max-w-centered-header mx-auto text-center section-padding-sm">
        <SectionLabel text={booking.label} />

        <h2 className="typography-hero-large leading-tight heading-body-spacing">
          {booking.headline}
        </h2>

        <p className="typography-body-lg leading-relaxed max-w-lg mx-auto">
          {booking.subline}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 card-grid-gap-sm md:card-grid-gap-md margin-top-md">
          {booking.formTopics.map((topic, i) => (
            <TopicCard key={i} title={topic} />
          ))}
        </div>

        <div className="margin-top-md">
          <button
            ref={buttonRef}
            onClick={open}
            className="inline-flex items-center gap-3 font-primary text-button-text py-4 px-12 rounded-full bg-color-primary text-primary-on-dark hover:bg-color-secondary transition-colors duration-200"
            data-testid="booking-cta-button"
          >
            {booking.directBookingCta}
            <ArrowUpRight className="chevron-on-dark" size={18} />
          </button>
        </div>
      </div>
      {overlay}
    </SectionWrapper>
  )
}

export function BookingSectionDark() {
  const { booking } = useContent()
  const { open, buttonRef, overlay } = useBookingModal(booking.calendarUrl)

  return (
    <SectionWrapper data-section="BookingSection"
      id="booking"
      data-testid="booking-section"
      className="bg-color-primary text-primary-on-dark"
    >
      <div className="max-w-centered-header mx-auto text-center section-padding-sm">
        <SectionLabel text={booking.label} light />

        <h2 className="typography-hero-large-on-dark leading-tight heading-body-spacing">
          {booking.headline}
        </h2>

        <p className="typography-body-lg-on-dark leading-relaxed max-w-lg mx-auto">
          {booking.subline}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 card-grid-gap-sm md:card-grid-gap-md margin-top-md">
          {booking.formTopics.map((topic, i) => (
            <TopicCard key={i} title={topic} dark />
          ))}
        </div>

        <div className="margin-top-md">
          <button
            ref={buttonRef}
            onClick={open}
            className="inline-flex items-center gap-3 font-primary text-button-text py-4 px-12 rounded-full bg-primary-on-dark text-color-primary hover:bg-color-secondary hover:text-primary-on-dark transition-all duration-200"
            data-testid="booking-cta-button"
          >
            {booking.directBookingCta}
            <ArrowUpRight size={18} />
          </button>
        </div>
      </div>
      {overlay}
    </SectionWrapper>
  )
}
